import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@langchain/untitled-ui-icons';
import { Autocomplete, Button, IconButton, Input } from '@mui/joy';

import { useEffect, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { PopoverDropdownMenu } from '@/components/PopoverDropdownMenu';
import { cn } from '@/utils/tailwind';

import {
  FILTER_COMPARATOR_TO_LABEL,
  FILTER_OPTION_TO_ICON,
  FILTER_OPTION_TO_LABEL,
} from '../constants';
import {
  FeedbackScoreSessionMetric,
  IncompleteSessionFilter,
  SessionFilterComparator,
  SessionFilterField,
  allowedSessionFeedbackComparators,
} from '../types/SessionsFilterModel';

export function SessionFilterBarOperands(props: {
  commonMetadata: Record<string, string[]>;
  commonFeedback: string[];
  currentFilters: IncompleteSessionFilter[];
  onChange: (operand: IncompleteSessionFilter[]) => void;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-2 text-xs">
      {props.currentFilters.map((filter, index) => {
        return (
          <div className="flex w-full items-center gap-2" key={index}>
            <span className="text-tertiary">{index > 0 ? 'And' : 'Where'}</span>
            <SessionFilterBarOperand
              commonMetadata={props.commonMetadata}
              commonFeedback={props.commonFeedback}
              key={index}
              filter={filter}
              onChange={(newFilter) => {
                if (newFilter === null) {
                  const newFilters = [...props.currentFilters];
                  newFilters.splice(index, 1);
                  props.onChange(newFilters);
                  return;
                }
                const newFilters = [...props.currentFilters];
                newFilters[index] = newFilter;
                props.onChange(newFilters);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function SessionFilterBarOperand(props: {
  commonMetadata: Record<string, string[]>;
  commonFeedback: string[];
  filter: IncompleteSessionFilter;
  onChange: (filter: IncompleteSessionFilter | null) => void;
}) {
  return (
    <div className="flex w-full justify-between">
      <div className="flex items-center gap-1">
        <PopoverDropdownMenu
          values={Object.values(SessionFilterField)}
          getIsSelected={(value) => value === props.filter.field}
          onSelect={(value) => {
            if (value === SessionFilterField.METADATA) {
              props.onChange({
                field: value,
                comparator: SessionFilterComparator.EXISTS,
                value: {},
              });
            } else {
              props.onChange({
                field: value,
                comparator: SessionFilterComparator.GT,
                value: '',
                metric: FeedbackScoreSessionMetric.AVERAGE,
              });
            }
          }}
          getTitle={(value) => FILTER_OPTION_TO_LABEL[value]}
          getIcon={(value) => FILTER_OPTION_TO_ICON[value]}
          asChild
        >
          <Button
            variant="outlined"
            color="neutral"
            endDecorator={<ChevronDownIcon className="h-4 w-4" />}
            size="sm"
            sx={{ fontSize: '12px', padding: '0px 6px', whiteSpace: 'nowrap' }}
          >
            {props.filter.field
              ? FILTER_OPTION_TO_LABEL[props.filter.field]
              : 'Select'}
          </Button>
        </PopoverDropdownMenu>
        {props.filter.field === SessionFilterField.METADATA ? (
          <SessionMetadataPicker
            commonMetadata={props.commonMetadata}
            value={props.filter.value as Record<string, string>}
            onChange={(value) => {
              props.onChange({
                field: SessionFilterField.METADATA,
                comparator: SessionFilterComparator.EXISTS,
                value,
              });
            }}
          />
        ) : props.filter.field === SessionFilterField.FEEDBACK_SCORE ? (
          <SessionFeedbackScorePicker
            value={props.filter}
            onChangeComparator={(comparator) => {
              props.onChange({ ...props.filter, comparator });
            }}
            onChangeKey={(key) => {
              props.onChange({ ...props.filter, key });
            }}
            onChangeValue={(value) => {
              props.onChange({ ...props.filter, value });
            }}
            commonFeedback={props.commonFeedback}
          />
        ) : null}
      </div>
      <IconButton
        size="sm"
        color="neutral"
        variant="plain"
        onClick={() => props.onChange(null)}
      >
        <XMarkIcon className="h-4 w-4" />
      </IconButton>
    </div>
  );
}

function SessionMetadataPicker(props: {
  commonMetadata: Record<string, string[]>;
  value: Record<string, string>;
  onChange: (value: Record<string, string | number>) => void;
}) {
  const currentCommonMetadataValues = Object.entries(
    props.commonMetadata
  ).reduce((acc, [, values]) => {
    const currentValues = new Set(values);
    for (const value of values) {
      currentValues.add(value);
    }
    return [...acc];
  }, []);
  currentCommonMetadataValues.sort();
  const currentCommonMetadataKeys = Object.keys(props.commonMetadata);
  currentCommonMetadataKeys.sort();
  const currentCommonMetadataValuesJson = JSON.stringify(
    currentCommonMetadataValues
  );
  const currentCommonMetadataKeysJson = JSON.stringify(
    currentCommonMetadataKeys
  );

  const [commonMetadata, setCommonMetadata] = useState(props.commonMetadata);

  useEffect(() => {
    // accumulate all metadata keys and values
    const currentCommonMetadataState = { ...commonMetadata };
    for (const [key, values] of Object.entries(props.commonMetadata)) {
      if (!Object.keys(currentCommonMetadataState).includes(key)) {
        currentCommonMetadataState[key] = values;
      } else {
        const currentValues = new Set(values);
        for (const value of values) {
          currentValues.add(value);
        }
        currentCommonMetadataState[key] = Array.from(currentValues);
      }
    }
    setCommonMetadata(currentCommonMetadataState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCommonMetadataValuesJson, currentCommonMetadataKeysJson]);

  const commonMetadataValues =
    commonMetadata[Object.keys(props.value)[0]] ?? [];

  const commonMetadataKeys = Object.keys(commonMetadata);

  return (
    <div className="line-clamp-1 flex items-center gap-1">
      <Autocomplete
        disableClearable
        freeSolo
        placeholder="key"
        options={commonMetadataKeys}
        value={Object.keys(props.value)[0] ?? ''}
        sx={{ width: 130, fontSize: '12px' }}
        size="sm"
        onInputChange={(_, newValue) => {
          props.onChange({ [newValue]: Object.values(props.value)[0] ?? '' });
        }}
        renderOption={(props, option, state) => {
          const { selected } = state;
          return (
            <li {...props}>
              <div
                className={cn(
                  'cursor-pointer px-3 py-2 hover:bg-secondary active:bg-tertiary',
                  selected &&
                    'bg-[#DDF1FF] text-[#044DA7] hover:bg-[#DDF1FF] hover:text-[#044DA7] dark:bg-[#02153C] dark:text-[#ADDBFF] dark:hover:bg-[#02153C] dark:hover:text-[#ADDBFF]'
                )}
              >
                <div className="line-clamp-1">{option}</div>
              </div>
            </li>
          );
        }}
        slotProps={{
          listbox: {
            sx: {
              wordBreak: 'break-all',
              minWidth: '200px',
              maxWidth: '200px',
            },
          },
        }}
      />
      <span>is</span>
      <Autocomplete
        disableClearable
        freeSolo
        placeholder="value"
        options={commonMetadataValues}
        renderOption={(props, option, state) => {
          const { selected } = state;
          return (
            <li {...props}>
              <div
                className={cn(
                  'cursor-pointer px-3 py-2 hover:bg-secondary active:bg-tertiary',
                  selected &&
                    'bg-[#DDF1FF] text-[#044DA7] hover:bg-[#DDF1FF] hover:text-[#044DA7] dark:bg-[#02153C] dark:text-[#ADDBFF] dark:hover:bg-[#02153C] dark:hover:text-[#ADDBFF]'
                )}
              >
                <div className="line-clamp-1">{option}</div>
              </div>
            </li>
          );
        }}
        value={Object.values(props.value)[0] ?? ''}
        sx={{ width: 130, fontSize: '12px' }}
        size="sm"
        slotProps={{
          listbox: {
            sx: {
              wordBreak: 'break-all',
              minWidth: '200px',
              maxWidth: '200px',
            },
          },
        }}
        onInputChange={(_, newValue) => {
          const key = Object.keys(props.value)[0];
          // if newValue can be parsed to a number, convert it to a number
          if (newValue !== '' && !isNaN(Number(newValue))) {
            props.onChange({
              [key]: Number(newValue),
            });
          } else {
            props.onChange({ [key]: newValue });
          }
        }}
      />
    </div>
  );
}

function SessionFeedbackScorePicker(props: {
  commonFeedback: string[];
  value: IncompleteSessionFilter;
  onChangeKey: (key: string) => void;
  onChangeValue: (value: string) => void;
  onChangeComparator: (comparator: SessionFilterComparator) => void;
}) {
  return (
    <div className="line-clamp-1 flex items-center gap-1">
      <Autocomplete
        disableClearable
        freeSolo
        placeholder="key"
        options={props.commonFeedback}
        value={props.value.key}
        sx={{ width: 130, fontSize: '12px' }}
        size="sm"
        onInputChange={(_, newValue) => {
          props.onChangeKey(newValue);
        }}
        renderOption={(props, option, state) => {
          const { selected } = state;
          return (
            <li {...props}>
              <div
                className={cn(
                  'cursor-pointer px-3 py-2 hover:bg-secondary active:bg-tertiary',
                  selected &&
                    'bg-[#DDF1FF] text-[#044DA7] hover:bg-[#DDF1FF] hover:text-[#044DA7] dark:bg-[#02153C] dark:text-[#ADDBFF] dark:hover:bg-[#02153C] dark:hover:text-[#ADDBFF]'
                )}
              >
                <div className="line-clamp-1">{option}</div>
              </div>
            </li>
          );
        }}
        slotProps={{
          listbox: {
            sx: {
              wordBreak: 'break-all',
              minWidth: '200px',
              maxWidth: '200px',
            },
          },
        }}
      />
      <SessionFilterCompSelect
        comps={allowedSessionFeedbackComparators}
        value={props.value.comparator ?? SessionFilterComparator.GT}
        onChange={props.onChangeComparator}
      />
      <Input
        value={props.value.value}
        onChange={(e) => props.onChangeValue(e.target.value)}
        sx={{ width: '100px', fontSize: '12px' }}
        size="sm"
      />
    </div>
  );
}

function SessionFilterCompSelect(props: {
  comps: SessionFilterComparator[];
  value: SessionFilterComparator;
  onChange: (value: SessionFilterComparator) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-secondary bg-background px-2 py-1.5'
        )}
      >
        {FILTER_COMPARATOR_TO_LABEL[props.value]}
        <ChevronDownIcon className="h-3 w-3 text-tertiary" />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="start"
          className="max-h-[50vh] overflow-y-auto"
        >
          {props.comps.map((field, i) => (
            <DropdownMenuItem
              key={i}
              className="flex items-center gap-1.5 text-sm"
              onClick={() => props.onChange(field)}
            >
              {FILTER_COMPARATOR_TO_LABEL[field]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
